/**
 * Preparation Status Hook - Context-Based
 *
 * Simple hook that calculates preparation status from shift data
 * already loaded in the ShiftDataProvider context.
 *
 * NO API calls, NO caching, NO subscriptions.
 * When shifts change in context, this automatically recalculates.
 */

import { useMemo } from 'react';
import { useUnifiedShift } from '@/components/shifts/context/ComposedShiftProvider';
import {
  calculatePreparationFromContext,
  type PreparationStatusFromContext,
  type DayPreparationStatus,
  type PeriodPreparationStatus,
  type AllPeriodStatuses,
  type VisibleDateRange
} from '@/lib/scheduling/calculate-preparation-from-context';

export interface UsePreparationStatusFromContextResult {
  // Day-level status lookup
  getDayStatus: (dateKey: string) => DayPreparationStatus | null;

  // Period-level statuses (for week/month indicators)
  periodStatuses: AllPeriodStatuses;

  // Individual period accessors for convenience
  currentWeek: PeriodPreparationStatus;
  nextWeek: PeriodPreparationStatus;
  currentMonth: PeriodPreparationStatus;
  nextMonth: PeriodPreparationStatus;

  // All day statuses (for iteration)
  dayStatuses: Map<string, DayPreparationStatus>;

  // Statistics
  stats: {
    totalDays: number;
    greenDays: number;
    orangeDays: number;
    redDays: number;
    closedDays: number;
  };

  // Loading state
  isLoading: boolean;
}

// Default empty period status
const emptyPeriodStatus: PeriodPreparationStatus = {
  hasShifts: false,
  allPrepared: false,
  shiftsCount: 0,
  daysWithShifts: 0,
  preparedDays: 0,
  partialDays: 0,
  unpreparedDays: 0
};

// Default empty result
const emptyResult: UsePreparationStatusFromContextResult = {
  getDayStatus: () => null,
  periodStatuses: {
    currentWeek: emptyPeriodStatus,
    nextWeek: emptyPeriodStatus,
    currentMonth: emptyPeriodStatus,
    nextMonth: emptyPeriodStatus
  },
  currentWeek: emptyPeriodStatus,
  nextWeek: emptyPeriodStatus,
  currentMonth: emptyPeriodStatus,
  nextMonth: emptyPeriodStatus,
  dayStatuses: new Map(),
  stats: {
    totalDays: 0,
    greenDays: 0,
    orangeDays: 0,
    redDays: 0,
    closedDays: 0
  },
  isLoading: true
};

/**
 * Hook that calculates preparation status from context data
 *
 * Must be used within a ShiftDataProvider context.
 * Automatically recalculates when shifts change.
 *
 * @param currentViewMonth - Optional: The month currently being viewed in the calendar.
 *                           Ensures day status is calculated for the viewed month.
 * @param visibleDateRange - Optional: The visible date range for infinite scroll mode.
 *                           When provided, expands calculation to cover all visible days.
 */
export const usePreparationStatusFromContext = (
  currentViewMonth?: Date,
  visibleDateRange?: VisibleDateRange
): UsePreparationStatusFromContextResult => {
  const {
    shifts,
    selectedBusinessDetails,
    loadingShifts,
    closedDatesMap,
    closedDatesLoading
  } = useUnifiedShift();

  const openingHours = selectedBusinessDetails?.opening_hours || [];

  const result = useMemo(() => {
    // Return empty while loading
    if (loadingShifts || closedDatesLoading || !shifts) {
      return null;
    }

    return calculatePreparationFromContext(shifts, openingHours, currentViewMonth, closedDatesMap, visibleDateRange);
  }, [shifts, openingHours, loadingShifts, currentViewMonth, closedDatesMap, closedDatesLoading, visibleDateRange]);

  // Return empty result while loading
  if (!result) {
    return emptyResult;
  }

  return {
    getDayStatus: result.getDayStatus,
    periodStatuses: result.periodStatuses,
    currentWeek: result.periodStatuses.currentWeek,
    nextWeek: result.periodStatuses.nextWeek,
    currentMonth: result.periodStatuses.currentMonth,
    nextMonth: result.periodStatuses.nextMonth,
    dayStatuses: result.dayStatuses,
    stats: result.stats,
    isLoading: false
  };
};

// Re-export types and helpers
export type {
  DayPreparationStatus,
  PeriodPreparationStatus,
  AllPeriodStatuses,
  VisibleDateRange
} from '@/lib/scheduling/calculate-preparation-from-context';

export {
  getPreparationStatusClass,
  getPeriodIndicatorColor
} from '@/lib/scheduling/calculate-preparation-from-context';
